import { ConnectError } from '@bufbuild/connect';
import {
  clearMaintenanceRedirectGuard,
  readMaintenanceDetails,
} from '@geneo2-web/services-clients';
import { Box, Button, Typography } from '@mui/material';
import { useEffect } from 'react';
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import darkNight from '../../../assets/images/dark-night.png';
import { ImageWrapper } from '../../elements/ImageWrapper';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';

const styles: IStyles = {
  screenRoot: {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '100vw',
    minHeight: '100vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(35), md: pxTovW(45) },
    paddingY: { xs: pxToRem(10), md: pxTovW(30) },
    paddingX: { xs: pxToRem(10), md: pxTovW(50) },
    boxSizing: 'border-box',
  },
  messageTypo: {
    fontSize: { xs: pxToRem(16), md: pxTovW(40) },
    display: '-webkit-box',
    WebkitLineClamp: { xs: 10, md: 3 },
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  buttonsBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(50) },
    width: '100%',
  },
  loggedInButtons: {
    width: { xs: '30%', md: pxTovW(270) },
    height: { xs: pxToRem(50), md: pxTovW(90) },
    backgroundColor: 'common.white',
    borderRadius: { xs: pxToRem(25), md: pxTovW(45) },
    '&:hover': {
      border: '2px solid #120c32',
      backgroundColor: 'common.white',
    },
    '&:active': {
      border: '2px solid #120c32',
      backgroundColor: 'common.white',
    },
    '&:focus': {
      border: '2px solid #120c32',
      backgroundColor: 'common.white',
    },
  },
  loggedOutButtons: {
    width: { xs: '30%', md: pxTovW(270) },
    height: { xs: pxToRem(50), md: pxTovW(90) },
    backgroundColor: 'common.white',
    borderRadius: { xs: pxToRem(25), md: pxTovW(45) },
    '&:hover': {
      border: '2px solid #00BBFF',
      backgroundColor: 'common.white',
    },
    '&:active': {
      border: '2px solid #00BBFF',
      backgroundColor: 'common.white',
    },
    '&:focus': {
      border: '2px solid #00BBFF',
      backgroundColor: 'common.white',
    },
  },
};

interface IRouteError {
  isLoggedIn: boolean;
  onLogoutClick?: () => void;
  onHomeClick?: () => void;
  onLoginClick?: () => void;
}

export function RouteError(props: IRouteError) {
  const { isLoggedIn, onLogoutClick, onHomeClick, onLoginClick } = props;
  console.log('RouteError props:', props);

  const err = useRouteError();
  console.error('RouteError:', err);

  let message = 'Something went wrong';

  if (isRouteErrorResponse(err)) {
    switch (err.status) {
      case 400:
        message = 'Bad request';
        break;

      case 401:
        message = 'Unauthorized access';
        break;

      case 403:
        message = 'You do not have permission to access this page';
        break;

      case 404:
        message = 'Page not found';
        break;

      case 408:
        message = 'Request timed out';
        break;

      case 500:
        message = 'Internal server error';
        break;

      case 502:
        message = 'Bad gateway';
        break;

      case 503:
        message = 'Service temporarily unavailable';
        break;

      default:
        message = `Unexpected error (HTTP ${err.status})`;
    }
  } else if (err instanceof ConnectError) {
    message = err.rawMessage;
  } else if (err instanceof Error) {
    message = err.message;
  }

  console.log('RouteError message:', message);

  if (isLoggedIn)
    return (
      <LoggedInScreen
        message={message}
        onLogoutClick={onLogoutClick}
        onHomeClick={onHomeClick}
      />
    );

  return <LoggedOutScreen message={message} onLoginClick={onLoginClick} />;
}

// ---------------- L O G G E D - I N - S C R E E N ----------------

const LoggedInScreen = ({
  message,
  onLogoutClick,
  onHomeClick,
}: {
  message: string;
  onLogoutClick?: () => void;
  onHomeClick?: () => void;
}) => {
  return (
    <Box sx={{ ...styles.screenRoot, backgroundColor: '#00BBFF' }}>
      <ImageWrapper
        name="sorry-error"
        type="png"
        parentFolder="images"
        styles={{
          width: { xs: '90%', md: pxTovW(600), lg: pxTovW(450) },
          height: { xs: '40%', md: pxTovW(600), lg: pxTovW(450) },
        }}
      />

      <Typography
        variant="h1"
        color="common.white"
        textAlign="center"
        sx={styles.messageTypo}
      >
        {message}
      </Typography>

      <Box sx={styles.buttonsBox}>
        <Button onClick={onLogoutClick} sx={styles.loggedInButtons}>
          <Typography variant="h2" fontWeight="regular">
            Sign out
          </Typography>
        </Button>

        <Button
          onClick={() => window.location.reload()}
          sx={styles.loggedInButtons}
        >
          <Typography variant="h2" fontWeight="regular">
            Refresh
          </Typography>
        </Button>

        <Button onClick={onHomeClick} sx={styles.loggedInButtons}>
          <Typography variant="h2" fontWeight="regular">
            Home
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

// --------------- L O G G E D - O U T - S C R E E N ---------------

const LoggedOutScreen = ({
  message,
  onLoginClick,
}: {
  message: string;
  onLoginClick?: () => void;
}) => {
  return (
    <Box sx={{ ...styles.screenRoot, backgroundImage: `url(${darkNight})` }}>
      <ImageWrapper
        name="astronaut"
        type="png"
        parentFolder="images"
        styles={{
          width: { xs: '90%', md: pxTovW(800), lg: pxTovW(500) },
          height: { xs: '50%', md: pxTovW(800), lg: pxTovW(500) },
        }}
      />

      <Typography
        variant="h1"
        color="common.white"
        textAlign="center"
        sx={styles.messageTypo}
      >
        {message}
      </Typography>

      <Box sx={styles.buttonsBox}>
        <Button onClick={onLoginClick} sx={styles.loggedOutButtons}>
          <Typography variant="h2" fontWeight="regular">
            Login
          </Typography>
        </Button>

        <Button
          onClick={() => window.location.reload()}
          sx={styles.loggedOutButtons}
        >
          <Typography variant="h2" fontWeight="regular">
            Refresh
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export function MaintenancePage({ homePath }: { homePath: string }) {
  const navigate = useNavigate();
  const { message } = readMaintenanceDetails();
  const detail =
    message === 'Application is under maintenance.' ? '' : message;

  useEffect(() => {
    clearMaintenanceRedirectGuard();
  }, []);

  return (
    <Box sx={{ ...styles.screenRoot, backgroundColor: '#00BBFF' }}>
      <ImageWrapper
        name="sorry-error"
        type="png"
        parentFolder="images"
        styles={{
          width: { xs: '90%', md: pxTovW(600), lg: pxTovW(450) },
          height: { xs: '40%', md: pxTovW(600), lg: pxTovW(450) },
        }}
      />

      <Typography
        variant="h1"
        color="common.white"
        textAlign="center"
        sx={styles.messageTypo}
      >
        Application is under maintenance.
      </Typography>
      {detail ? (
        <Typography
          variant="h2"
          color="common.white"
          textAlign="center"
          fontWeight="regular"
          sx={styles.messageTypo}
        >
          {detail}
        </Typography>
      ) : null}

      <Box sx={styles.buttonsBox}>
        <Button onClick={() => navigate(-1)} sx={styles.loggedInButtons}>
          <Typography variant="h2" fontWeight="regular">
            Go back
          </Typography>
        </Button>

        <Button onClick={() => navigate(homePath)} sx={styles.loggedInButtons}>
          <Typography variant="h2" fontWeight="regular">
            Home
          </Typography>
        </Button>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------
